import { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { MonitorSmartphone, KeyRound, Building2, UserCog } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PageHeader, PlanStatusBadge } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import { api, fmtDateTime, fmtDate, isAdminRole } from "@/lib/api";
import { AppearanceTab } from "@/components/settings/AppearanceTab";
import { AuditTab } from "@/components/settings/AuditTab";
import { PrintingTab } from "@/components/settings/PrintingTab";
import { WhatsAppTab } from "@/components/settings/WhatsAppTab";
import { TourButton } from "@/components/tour/TourButton";

function SecurityTab({ user }) {
  const [sessions, setSessions] = useState([]);
  const load = useCallback(() => api.get("/auth/sessions").then((r) => setSessions(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const revoke = async (id) => { await api.post(`/auth/sessions/${id}/revoke`); load(); };
  return (
    <div className="space-y-4">
      <div className="card-surface p-5 text-sm space-y-1" data-testid="last-access-info">
        <p className="eyebrow mb-2">Registro de acesso</p>
        <p>Acesso atual: <b>{fmtDateTime(user.last_login_at)}</b> · {user.last_login_device}</p>
        <p>Acesso anterior: {user.previous_login?.at ? <><b>{fmtDateTime(user.previous_login.at)}</b> · {user.previous_login.device}</> : "—"}</p>
      </div>
      <div className="card-surface">
        <p className="border-b p-4 eyebrow">Sessões recentes</p>
        <ul className="divide-y" data-testid="sessions-list">
          {sessions.map((s) => (
            <li key={s.id} className="flex flex-wrap items-center gap-3 p-4 text-sm">
              <MonitorSmartphone className="h-5 w-5 text-muted-foreground" />
              <div className="flex-1 min-w-[180px]"><p className="font-medium">{s.device} {s.current && <span className="text-xs text-emerald-500">(esta sessão)</span>}</p>
                <p className="text-xs text-muted-foreground">{fmtDateTime(s.created_at)} · IP {s.ip || "—"} · {s.revoked ? "Encerrada" : s.expired ? "Expirada" : "Ativa"}</p></div>
              {!s.revoked && !s.current && !s.expired && <Button size="sm" variant="outline" onClick={() => revoke(s.id)} data-testid={`revoke-session-${s.id}`}>Encerrar</Button>}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

export default function SettingsPage() {
  const { session } = useAuth();
  const isManager = isAdminRole(session.user);
  const sub = session.subscription;
  const [params] = useSearchParams();
  const aba = params.get("aba") || "aparencia";
  return (
    <>
      <PageHeader eyebrow="Sistema" title="Configurações" actions={<>
        <TourButton testId="settings-tour-btn" />
        {isManager && <>
        <Button asChild variant="outline" data-testid="settings-go-company-btn"><Link to="/app/sistema/empresa"><Building2 className="h-4 w-4 mr-1" />Minha empresa</Link></Button>
        <Button asChild variant="outline" data-testid="settings-go-users-btn"><Link to="/app/sistema/usuarios"><UserCog className="h-4 w-4 mr-1" />Usuários</Link></Button>
      </>}</>} />
      <Tabs key={aba} defaultValue={aba}>
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="aparencia" data-testid="settings-tab-aparencia">Aparência</TabsTrigger>
          <TabsTrigger value="seguranca" data-testid="settings-tab-seguranca">Segurança</TabsTrigger>
          <TabsTrigger value="assinatura" data-testid="settings-tab-assinatura">Assinatura</TabsTrigger>
          {isManager && <TabsTrigger value="impressao" data-testid="settings-tab-impressao">Impressão</TabsTrigger>}
          {isManager && <TabsTrigger value="whatsapp" data-testid="settings-tab-whatsapp">WhatsApp Bot</TabsTrigger>}
          {isManager && <TabsTrigger value="logs" data-testid="settings-tab-logs">Logs e backup</TabsTrigger>}
        </TabsList>
        <TabsContent value="aparencia" className="mt-4"><AppearanceTab isAdmin={isManager} /></TabsContent>
        {isManager && <TabsContent value="impressao" className="mt-4"><PrintingTab /></TabsContent>}
        {isManager && <TabsContent value="whatsapp" className="mt-4"><WhatsAppTab /></TabsContent>}
        {isManager && <TabsContent value="logs" className="mt-4"><AuditTab /></TabsContent>}
        <TabsContent value="seguranca" className="mt-4"><SecurityTab user={session.user} /></TabsContent>
        <TabsContent value="assinatura" className="mt-4">
          <div className="card-surface p-5 space-y-3 text-sm" data-testid="settings-subscription">
            <div className="flex items-center gap-3"><KeyRound className="h-5 w-5 text-primary" /><span className="font-mono text-lg tracking-widest" data-testid="settings-access-number">{session.tenant.access_number}</span><PlanStatusBadge status={sub.status} testId="settings-plan-status" /></div>
            <p>Início do teste: <b>{fmtDate(sub.trial_start)}</b> · Término: <b>{fmtDate(sub.trial_end)}</b> · Dias restantes: <b>{sub.days_left}</b></p>
            {sub.plan && <p>Plano contratado: <b className="capitalize">{sub.plan}</b></p>}
            <Button asChild data-testid="settings-go-plans-btn"><Link to="/app/planos">Ver planos</Link></Button>
          </div>
        </TabsContent>
      </Tabs>
    </>
  );
}
